import React, { createContext, useState, useCallback, useEffect } from 'react';
import { api } from '../services/baseUrl';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, items_count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger le panier depuis l'API
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setCart({ items: [], total: 0, items_count: 0 });
        return;
      }

      const response = await api.get('/cart');

      if (response.status === 401) {
          localStorage.removeItem('access_token');
          setCart({ items: [], total: 0, items_count: 0 });
          return;
      }

      const data = response.data;
      if (data.success && data.cart) {
        setCart(data.cart);
      }
      setError(null);
    } catch (err) {
      console.error('Erreur fetchCart:', err);
      const message = err.response?.data?.message || err.message;
      setError(message || 'Impossible de joindre l’API. Vérifiez que le backend est démarré.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Ajouter un produit au panier
  const addToCart = useCallback(async (productId, quantity = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Vous devez être connecté pour ajouter au panier');
        return false;
      }

      const response = await api.post('/cart/add', {
        product_id: productId,
        quantity: quantity,
      });

      const data = response.data;

      if (!response || response.status >= 400) {
        let errorMessage = data?.message || 'Erreur lors de l\'ajout au panier';
        
        // Si validation errors
        if (data?.errors) {
          errorMessage = Object.values(data.errors).flat().join(', ');
        }
        
        throw new Error(errorMessage);
      }

      if (data.success && data.cart) {
        setCart(data.cart);
      }
      setError(null);
      return data.success;
    } catch (err) {
      console.error('Erreur addToCart:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Erreur lors de l\'ajout au panier (réseau)';
      setError(errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mettre à jour la quantité d'un article
  const updateQuantity = useCallback(async (itemId, quantity) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Vous devez être connecté');
        return;
      }

      const response = await api.put(`/cart/items/${itemId}`, { quantity });

      const data = response.data;

      if (data.success && data.cart) {
        setCart(data.cart);
      }
      setError(null);
      return data.success;
    } catch (err) {
      console.error('Erreur updateQuantity:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg || 'Erreur lors de la mise à jour');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Supprimer un article du panier
  const removeFromCart = useCallback(async (itemId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Vous devez être connecté');
        return;
      }

      const response = await api.delete(`/cart/items/${itemId}`);

      const data = response.data;

      if (data.success && data.cart) {
        setCart(data.cart);
      }
      setError(null);
      return data.success;
    } catch (err) {
      console.error('Erreur removeFromCart:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg || 'Erreur lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Vider le panier
  const clearCart = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Vous devez être connecté');
        return;
      }

      const response = await api.delete('/cart/clear');

      const data = response.data;

      if (data.success && data.cart) {
        setCart(data.cart);
      }
      setError(null);
      return data.success;
    } catch (err) {
      console.error('Erreur clearCart:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg || 'Erreur lors du vidage du panier');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger le panier au montage et quand le token change
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const value = {
    cart,
    loading,
    error,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
